import { DateTime } from 'luxon';
import { useProfileBridgeStore } from '@/rennovation/planning-view/store/bridge/profileBridge';
import { useDateTime } from '../../../../components/composables/useDateTime.js';

/**
 * A reusable date formatter composable using native Intl API.
 * Follows the "Strict Island" pattern for Phase 1 renovation.
 */
export function useDateFormatter() {
	const profileBridge = useProfileBridgeStore();
	const { formatTime, applyPlanningTimezone } = useDateTime();

	// Create persistent formatters for performance (reusing objects is faster)
	const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
		year: '2-digit',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
	});

	const dateFormatter = new Intl.DateTimeFormat('en-US', {
		year: '2-digit',
		month: '2-digit',
		day: '2-digit',
	});

	/**
	 * Formats a date string or object into: MM/DD/YY hh:mm AM/PM
	 */
	const formatDateTime = (value: string | Date | undefined | null): string => {
		if (!value) return '';
		const date = typeof value === 'string' ? new Date(value) : value;

		// Check for Invalid Date
		if (isNaN(date.getTime())) return '';

		return dateTimeFormatter.format(date);
	};

	/**
	 * Formats a date string or object into: MM/DD/YY
	 */
	const formatDate = (value: string | Date | undefined | null): string => {
		if (!value) return '';
		const date = typeof value === 'string' ? new Date(value) : value;

		if (isNaN(date.getTime())) return '';

		return dateFormatter.format(date);
	};

	/**
	 * Formats a date string (e.g. "2026-08-20" or an ISO date) using the current
	 * locale's configured date format (profileBridge.locale.dateFormat), parsed
	 * and rendered via Luxon — matching the pattern in UserAbsences.vue's
	 * getDatetime(), rather than hardcoding a format.
	 */
	const formatDateByLocale = (value: string | null | undefined): string => {
		if (!value) return '';

		const dateFormat = profileBridge.locale?.dateFormat;
		if (!dateFormat) return '';

		const parsed = DateTime.fromISO(value);
		if (!parsed.isValid) return '';

		return parsed.toFormat(dateFormat);
	};

	/**
	 * Formats a 24-hour time string ("HH:mm") using the current locale's
	 * configured time format (Vuex `state.profile.locale.timeFormat`),
	 * rather than hardcoding AM/PM logic. Parsed and formatted via Luxon,
	 * consistent with how UserAbsences.vue formats dates/times elsewhere.
	 *
	 * @param {string | null | undefined} value - Time string in 24-hour format (e.g. "14:30").
	 * @returns {string} The locale-formatted time string, or '' if value is missing/invalid.
	 */
	const formatTimeByLocale = (value: string | null | undefined): string => {
		if (!value) return '';
		const parsed = formatTime(applyPlanningTimezone(DateTime.fromISO(value)));
		return parsed;
	};

	return {
		formatDateTime,
		formatDate,
		formatTimeByLocale,
		formatDateByLocale,
	};
}
